import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Button, Card, Col, Container, Form, Row, Spinner, Table } from 'react-bootstrap'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { fmtDays, toBE } from './hrShared'

export default function HrLeaveReport() {
  const currentYear = new Date().getFullYear()
  const [year, setYear] = useState(String(currentYear))
  const [report, setReport] = useState({ types: [], rows: [] })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const fetchReport = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await axiosInstance.get(`/api/hr/reports/leave-summary?year=${year}`)
      if (res.data.success) setReport(res.data.data)
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดรายงานไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [year])

  useEffect(() => { fetchReport() }, [fetchReport])

  // Pivot rows (teacher × type) into one row per teacher
  const teachers = []
  const byTeacher = new Map()
  for (const r of report.rows) {
    if (!byTeacher.has(r.t_id)) {
      const t = { t_id: r.t_id, t_name: r.t_name, t_idnumber: r.t_idnumber, byType: {}, total: 0 }
      byTeacher.set(r.t_id, t)
      teachers.push(t)
    }
    const t = byTeacher.get(r.t_id)
    t.byType[r.lt_id] = parseFloat(r.used_days)
    t.total += parseFloat(r.used_days)
  }

  const yearOptions = [currentYear + 1, currentYear, currentYear - 1, currentYear - 2]

  const escHtml = (s) => String(s ?? '').replace(/[&<>"']/g, c =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]))

  const handlePrint = () => {
    const title = `รายงานสรุปการลา ประจำปี พ.ศ. ${toBE(year)}`
    const thStyle = 'border:1px solid #999;padding:4px 6px;background:#ddeeff;'
    const tdStyle = 'border:1px solid #999;padding:4px 6px;'
    const headCells = report.types
      .map(t => `<th style="${thStyle}text-align:center;">${escHtml(t.lt_name)}</th>`).join('')
    const bodyRows = teachers.length === 0
      ? `<tr><td colspan="${report.types.length + 2}" style="${tdStyle}text-align:center;">ไม่มีข้อมูลการลาที่อนุมัติในปีนี้</td></tr>`
      : teachers.map(t => `
        <tr>
          <td style="${tdStyle}">${escHtml(t.t_name)} <span style="color:#666;font-size:0.8em;">${escHtml(t.t_idnumber)}</span></td>
          ${report.types.map(ty =>
            `<td style="${tdStyle}text-align:center;">${t.byType[ty.lt_id] ? escHtml(fmtDays(t.byType[ty.lt_id])) : '-'}</td>`).join('')}
          <td style="${tdStyle}text-align:center;font-weight:bold;">${escHtml(fmtDays(t.total))}</td>
        </tr>`).join('')
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${escHtml(title)}</title>
      <style>
        body { font-family: 'Cordia New', Tahoma, Arial, sans-serif; font-size: 14px; padding: 12px; }
        table { border-collapse: collapse; width: 100%; }
      </style></head>
      <body onload="window.print()">
        <h3 style="text-align:center;margin-bottom:12px;">${escHtml(title)} (หน่วย: วัน)</h3>
        <table><thead><tr>
          <th style="${thStyle}text-align:left;">ชื่อ-สกุล</th>
          ${headCells}
          <th style="${thStyle}text-align:center;">รวม</th>
        </tr></thead>
        <tbody>${bodyRows}</tbody>
        </table>
      </body></html>`
    const win = window.open('', '_blank', 'width=1200,height=900')
    win.document.write(html)
    win.document.close()
  }

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between mb-4 d-print-none">
            <h4 className="main-title mb-0">
              <i className="ri-bar-chart-box-line me-2 text-primary" />
              รายงานสรุปการลา
            </h4>
            <div className="d-flex gap-2">
              <Form.Select size="sm" style={{ width: 140 }} value={year} onChange={e => setYear(e.target.value)}>
                {yearOptions.map(y => <option key={y} value={y}>ปี พ.ศ. {toBE(y)}</option>)}
              </Form.Select>
              <Button size="sm" variant="outline-primary" onClick={handlePrint} disabled={loading}>
                <i className="ri-printer-line me-1" />พิมพ์
              </Button>
            </div>
          </div>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

          <Card className="card-one">
            <Card.Header>
              <Card.Title as="h6" className="mb-0">
                สรุปวันลาที่ได้รับอนุมัติ ประจำปี พ.ศ. {toBE(year)} (หน่วย: วัน)
              </Card.Title>
            </Card.Header>
            <Card.Body className="p-0">
              {loading ? (
                <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
              ) : (
                <Table responsive bordered hover className="mb-0 fs-sm">
                  <thead className="table-light">
                    <tr>
                      <th style={{ minWidth: 180 }}>ชื่อ-สกุล</th>
                      {report.types.map(t => <th key={t.lt_id} className="text-center">{t.lt_name}</th>)}
                      <th className="text-center table-secondary">รวม</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teachers.length === 0 ? (
                      <tr>
                        <td colSpan={report.types.length + 2} className="text-center text-muted py-4">
                          ไม่มีข้อมูลการลาที่อนุมัติในปีนี้
                        </td>
                      </tr>
                    ) : teachers.map(t => (
                      <tr key={t.t_id}>
                        <td>
                          {t.t_name}
                          <div className="text-muted" style={{ fontSize: '0.75rem' }}>{t.t_idnumber}</div>
                        </td>
                        {report.types.map(ty => (
                          <td key={ty.lt_id} className="text-center">
                            {t.byType[ty.lt_id] ? fmtDays(t.byType[ty.lt_id]) : '-'}
                          </td>
                        ))}
                        <td className="text-center fw-bold table-secondary">{fmtDays(t.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Container>
      </div>
      <Footer />
    </>
  )
}
